/**
 * WorldCupCountdown — strip under the header that surfaces the current
 * World Cup context. Four states:
 *
 *   pre        — counts down to the opening match (Jun 11, 2026).
 *                Identical to the v1 behaviour ("World Cup kickoff in 17d 05h").
 *   match-live — a match is currently being played. Shows team labels +
 *                elapsed minute. Pulsing red dot. Ticks every second.
 *   match-next — a match kicks off in <24h. Shows team labels + countdown.
 *                Ticks every minute (or every second in the final 60min).
 *   match-ft   — a match finished within the last 6h. Shows the score, if
 *                entered via /api/admin/wc-results, else "FT" status.
 *                Static (no tick).
 *   during     — WC is underway but no match is in the next-24h or
 *                post-match window. Falls back to "World Cup final in Xd Yh".
 *   post       — WC is over (after final). Renders nothing.
 *
 * Pauses ticking when the tab is hidden. Trusts the device clock (acceptable
 * for v1 per the prior comment block).
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  type CountdownPhase,
  formatCountdownString,
  formatRemaining,
  getCountdown,
} from "@/lib/worldCup";
import {
  type MatchBannerState,
  type WCMatch,
  displayTeam,
  getMatchBannerState,
} from "@/lib/wc-fixtures";

function pickIntervalMs(
  phase: CountdownPhase,
  remainingMs: number,
  bannerState: MatchBannerState | null,
): number {
  if (phase === "post") return 0;
  // Match in progress — tick every second to update the elapsed minute.
  if (bannerState?.kind === "live") return 1_000;
  // Match within an hour of kickoff — tick every second.
  if (bannerState?.kind === "next" && bannerState.seconds <= 3_600) return 1_000;
  // Match within 24h of kickoff — tick every minute.
  if (bannerState?.kind === "next") return 60_000;
  // Recently finished — no tick needed; the FT banner is static.
  if (bannerState?.kind === "finished") return 60_000;
  // Default to the v1 logic for pre/during without a match window.
  return remainingMs > 24 * 60 * 60 * 1000 ? 60_000 : 1_000;
}

interface MatchLabelParts {
  team1: string;
  team2: string;
  venue: string;
}

function matchLabel(match: WCMatch): MatchLabelParts {
  return {
    team1: displayTeam(match.team1),
    team2: displayTeam(match.team2),
    venue: match.venue,
  };
}

/**
 * Format the live-match minute. Soccer convention: 0–45 first half,
 * 45+x stoppage, half-time, 45–90 second half, 90+x stoppage.
 * Simple approximation: floor(seconds / 60) with no half-time gap.
 */
function liveMinute(seconds: number): string {
  const m = Math.floor(seconds / 60);
  if (m <= 45) return `${m}'`;
  // Compressed: assume 5min stoppage + 15min HT = 20min, then second half starts at "46'"
  if (m <= 65) return `45+${m - 45}'`;
  if (m <= 110) return `${m - 20}'`;
  return `90+${m - 110}'`;
}

/** Pad seconds → "Xh Ym" or "Ym Zs". Same shape used by the v1 countdown. */
function formatSeconds(seconds: number): string {
  if (seconds <= 0) return "0s";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3_600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3_600);
  const m = Math.floor((seconds % 3_600) / 60);
  return m > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${h}h`;
}

/**
 * Routes that embed their own WC countdown and should NOT show the global
 * banner. Mirrors the GlobalUserPill suppression pattern in
 * components/GlobalUserPill.tsx so the two layouts stay aligned.
 *
 *   /play — PlayPage's canvas header already has a 3-column banner with
 *   a centred CURVLE wordmark + countdown sized for the narrow column
 *   (mobile-vs-desktop variants). The global strip would duplicate it.
 */
const SUPPRESSED_PATHS = new Set<string>([
  "/play",
]);

export function WorldCupCountdown() {
  const { pathname } = useLocation();
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const { phase, remainingMs } = getCountdown(now);
    if (phase === "post") return;

    const bannerState = phase === "during" ? getMatchBannerState(now) : null;
    const intervalMs = pickIntervalMs(phase, remainingMs, bannerState);
    if (intervalMs === 0) return;

    let id: number | null = null;
    const start = () => {
      if (id !== null) return;
      id = window.setInterval(() => setNow(Date.now()), intervalMs);
    };
    const stop = () => {
      if (id !== null) {
        window.clearInterval(id);
        id = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        setNow(Date.now());
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [now]);

  if (SUPPRESSED_PATHS.has(pathname)) return null;

  const state = getCountdown(now);
  if (state.phase === "post" || !state.label) return null;

  // PRE phase: identical to v1 behaviour. Don't break what works.
  if (state.phase === "pre") {
    const parts = formatRemaining(state.remainingMs);
    const text = formatCountdownString(parts);
    return (
      <div
        className="flex items-center justify-center gap-2 px-4 py-1.5 text-xs opacity-75"
        role="status"
        aria-live="off"
      >
        <span aria-hidden="true">⚽</span>
        <span>
          World Cup kickoff in{" "}
          <span className="font-semibold tabular-nums">{text}</span>
        </span>
      </div>
    );
  }

  // DURING phase: check for an active match window first.
  const banner = getMatchBannerState(now);

  if (banner?.kind === "live") {
    const { team1, team2 } = matchLabel(banner.match);
    return (
      <div
        className="flex items-center justify-center gap-2 px-4 py-1.5 text-xs"
        role="status"
        aria-live="off"
      >
        <span
          aria-label="live"
          className="inline-block h-2 w-2 animate-pulse rounded-full bg-red-500"
        />
        <span className="font-semibold text-red-200">LIVE</span>
        <span className="opacity-90">
          {team1} vs {team2}
          <span className="ml-2 font-semibold tabular-nums">
            {liveMinute(banner.seconds)}
          </span>
        </span>
      </div>
    );
  }

  if (banner?.kind === "next") {
    const { team1, team2 } = matchLabel(banner.match);
    return (
      <div
        className="flex items-center justify-center gap-2 px-4 py-1.5 text-xs opacity-90"
        role="status"
        aria-live="off"
      >
        <span aria-hidden="true">⚽</span>
        <span>
          Next:{" "}
          <span className="font-semibold">
            {team1} vs {team2}
          </span>{" "}
          <span className="opacity-75">·</span>{" "}
          <span className="font-semibold tabular-nums">
            {formatSeconds(banner.seconds)}
          </span>
        </span>
      </div>
    );
  }

  if (banner?.kind === "finished") {
    const { team1, team2 } = matchLabel(banner.match);
    // TODO(scores): once /api/admin/wc-results stores results, fetch and
    // show "FT: GER 2 – 1 SCO". Until then, surface "FT" + teams.
    return (
      <div
        className="flex items-center justify-center gap-2 px-4 py-1.5 text-xs opacity-75"
        role="status"
        aria-live="off"
      >
        <span aria-hidden="true">⚽</span>
        <span>
          FT:{" "}
          <span className="font-semibold">
            {team1} vs {team2}
          </span>
        </span>
      </div>
    );
  }

  // During WC, no match window → fall back to the headline countdown
  // to the Final (identical to v1 during-phase behaviour).
  const parts = formatRemaining(state.remainingMs);
  const text = formatCountdownString(parts);
  return (
    <div
      className="flex items-center justify-center gap-2 px-4 py-1.5 text-xs opacity-75"
      role="status"
      aria-live="off"
    >
      <span aria-hidden="true">⚽</span>
      <span>
        World Cup final in{" "}
        <span className="font-semibold tabular-nums">{text}</span>
      </span>
    </div>
  );
}
